/**
 * Report intents: "which document?", build one, and "which machine in this
 * report should I look at first?".
 *
 * The regex engine treated any question containing "report" plus almost any
 * word (the verb list included "report" itself) as a request for a document,
 * so asking *about* the open report produced the six-document picker. Here a
 * build needs a generate verb and a document noun; "this report" is a
 * question about the report on the page (or the one just built).
 */

import { FLEET, MINES } from '../../../data/core.ts';
import type { MineCode } from '../../../schema/machine.ts';
import type { ReportRef } from '../../../schema/report.ts';
import { isWeeklyKind, reportRef, WEEKLY_DIVISION } from '../../report-catalog.ts';
import type { OemAnswerAction } from '../answer-types.ts';
import type { Entities } from '../entities.ts';
import { TOOLS, type ReportKind } from '../tools.ts';
import { cues, type IntentDef, type ScoreCtx } from './types.ts';

/**
 * Demo line 4068. The six buttons the "which document?" answer offers, with
 * the exact prompt each sends back into the assistant. Re-exported from
 * `../local-answer.ts`.
 */
export const REPORT_CHOICES: readonly OemAnswerAction[] = [
  { label: 'Risk email · Mount Isolde', prompt: 'Write the machine risk email for Mount Isolde' },
  {
    label: 'Weekly · Underground Drilling',
    prompt: 'Generate the weekly status report for Underground Drilling',
  },
  { label: 'Weekly · Load & Haul', prompt: 'Generate the weekly status report for Load & Haul' },
  {
    label: 'Weekly · Surface Drilling',
    prompt: 'Generate the weekly status report for Surface Drilling',
  },
  {
    label: 'Deck · Broken Ridge',
    prompt: 'Create the Broken Ridge equipment summary presentation',
  },
  { label: 'S.O.S daily report', prompt: "Generate today's S.O.S report" },
];

const GENERATE =
  /\b(generate|create|make|build|write|draft|prepare|produce|give me|run|send|put together|can you|could you|please|i need|i want|new)\b/;
const DOC_NOUN = /\b(report|e-?mail|deck|presentation|slides|powerpoint|pptx|status report|s\.?o\.?s)\b/;

/** What kind of document a question asks for, or `null`. `'ask'` = which one? */
export function reportRequest(L: string, E: Entities): { kind: ReportKind | 'ask'; mine: MineCode | null } | null {
  if (/\b(this|the) report\b/.test(L) && !GENERATE.test(L)) return null;
  const mine = E.mine ?? null;
  if (/s\.?o\.?s\b|hotspot report|daily report|morning report|service operations summary/.test(L) && GENERATE.test(L)) {
    return { kind: 'sos', mine };
  }
  if (!GENERATE.test(L) || !DOC_NOUN.test(L)) return null;
  if (/\be-?mail\b/.test(L)) return { kind: 'email', mine: mine ?? 'MI' };
  if (/\b(deck|presentation|slides|slide deck|powerpoint|pptx)\b/.test(L)) return { kind: 'deck', mine: mine ?? 'BR' };
  if (!/\b(weekly|status report|weekly status|report)\b/.test(L)) return null;
  if (/surface|blasthole|top-?hammer|open pit|dth/.test(L)) return { kind: 'surface', mine: null };
  if (/drill|jumbo|production rig|longhole|development/.test(L)) return { kind: 'drills', mine: null };
  if (/load|haul|loader|truck|lhd/.test(L)) return { kind: 'loadhaul', mine: null };
  if (/risk/.test(L) && mine) return { kind: 'email', mine };
  return { kind: 'ask', mine };
}

const scoreReport =
  (want: 'ask' | 'build') =>
  (L: string, E: Entities, _ctx: ScoreCtx): number => {
    const r = reportRequest(L, E);
    if (!r) return 0;
    return (r.kind === 'ask') === (want === 'ask') ? 7 : 0;
  };

// ---------------------------------------------------------------------------
// report_pick — demo L4068
// ---------------------------------------------------------------------------

export const reportPick: IntentDef = {
  id: 'report_pick',
  family: 'reports',
  priority: 80,
  examples: ['Can you make me a report?'],
  score: scoreReport('ask'),
  async answer({ kit }) {
    kit.ran('report_intent');
    return kit.done('I can build six documents from the live workspace data — which one?', [], {
      actions: REPORT_CHOICES,
    });
  },
};

// ---------------------------------------------------------------------------
// report_build — demo L4069
// ---------------------------------------------------------------------------

export const reportBuild: IntentDef = {
  id: 'report_build',
  family: 'reports',
  priority: 81,
  examples: ['Generate the weekly status report for Load & Haul', 'Write the machine risk email for Mount Isolde'],
  score: scoreReport('build'),
  async answer({ kit, E, L }) {
    const ri = reportRequest(L, E)!;
    const built = await kit.run(TOOLS.generate_report, { kind: ri.kind as ReportKind, mine: ri.mine ?? null });
    const file = built.download
      ? `The ${built.download === 'eml' ? '.eml' : 'HTML file'} is ready to download from the card below.`
      : 'It renders in the workspace; every figure opens its rows.';
    const markdown =
      `**${built.title}** is open in the workspace — ${built.subtitle}.\n\n` +
      built.headline.map((line) => `- ${line}`).join('\n') +
      `\n\n${file}`;
    kit.remember({ report: { kind: built.kind, mine: built.mine } });
    if (built.mine) kit.focus({ mine: built.mine });
    return kit.done(markdown, ['generate_report'], {
      navigate: built.route,
      report: {
        kind: built.kind,
        title: built.title,
        subtitle: built.subtitle,
        label: built.label,
        format: built.format,
        download: built.download,
        route: built.route,
        steps: built.steps.map((s) => ({ ...s })),
      },
    });
  },
};

// ---------------------------------------------------------------------------
// report_focus — new: "which machine in this report should I look at first?"
// ---------------------------------------------------------------------------

/** The report on the page, else the one this conversation last built. */
function reportInView(ctx: ScoreCtx): { ref: ReportRef; from: 'page' | 'memory' } | null {
  if (ctx.page.report && ctx.page.report.kind !== 'sos') return { ref: ctx.page.report, from: 'page' };
  const m = ctx.memory?.report as { kind?: string; mine?: string | null } | undefined;
  if (m?.kind && m.kind !== 'sos') return { ref: reportRef(m.kind, m.mine), from: 'memory' };
  return null;
}

export const reportFocus: IntentDef = {
  id: 'report_focus',
  family: 'reports',
  priority: 82,
  examples: ['Which machine in this report should I look at first?'],
  score: cues({
    any: [/(in|on|from) (this|the) report|this report/],
    all: [/which|what|who|where|first|worst|priority|focus|look at|start/],
    base: 7,
  }),
  async answer(ctx) {
    const { kit } = ctx;
    const inView = reportInView(ctx);
    const ref = inView?.ref;
    const mine = ref && !isWeeklyKind(ref.kind) ? (ref.mine as MineCode) : null;
    const division = ref && isWeeklyKind(ref.kind) ? WEEKLY_DIVISION[ref.kind] : null;
    const cls = division ? [...new Set(FLEET.filter((f) => f.division === division).map((f) => f.cls))].join(',') : null;
    const r = await kit.run(TOOLS.predict_risk, { mine, ...(cls ? { cls } : {}) });
    const top = r[0];
    const scope = mine ? MINES[mine].name : (division ?? 'the whole fleet');
    const note = inView
      ? inView.from === 'page'
        ? '(the report on this page)'
        : '(the report I just built)'
      : '— no report is open, so this ranks the whole fleet';
    if (!top) return kit.done(`Nothing in ${scope} scores any failure risk.`, ['predict_risk']);
    kit.focus({ machine: top.machine, mine: top.mine });
    const markdown =
      `**Start with ${top.machine}** (${top.model}, ${MINES[top.mine].name}) — risk **${top.risk}**: ${top.factors.join('; ') || 'no single driver'}. ` +
      `${top.recommendation}.\n\n` +
      `Scope: ${scope} ${note}. Next: ${r
        .slice(1, 4)
        .map((x) => `${x.machine} ${x.risk}`)
        .join(', ')}.`;
    return kit.done(markdown, ['predict_risk', 'component_health', 'daily telemetry']);
  },
};
